/**
 * Cache Manager - V25.3.0 SILENT UPDATE SYSTEM
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * V25.3.0 CHANGES:
 * - ALL window.location.reload() calls REMOVED permanently
 * - Integrated with silentUpdateManager for seamless updates
 * - Updates activate on safe navigation/idle only
 * - NO blinking, NO interruption, NO user action required
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import silentUpdateManager from './silentUpdateManager';

import devLog from './devLog';

// ═══════════════════════════════════════════════════════════════════════════════
// VERSION CONSTANTS - V25.5.2 FORCE FRESH CONTENT
// ═══════════════════════════════════════════════════════════════════════════════
export const BUILD_VERSION = '37.44.46';
// V37.44.46: Moved Project History / Bug Fix History links from the Header right-cluster to a dedicated row above the privacy notice on the Home page (b1 position per UX refinement). Header retains the links on non-home routes as "Project Prompts" / "Bug Fix Prompts".
export const BUILD_ID = 'v37.44.46-home-history-row';
export const BUILD_TIMESTAMP = '2026-05-05T18:00:00Z';
// ═══════════════════════════════════════════════════════════════════════════════

// Storage keys
const CACHE_VERSION_KEY = 'ep_cache_version';
const LAST_CHECK_KEY = 'ep_last_version_check';
const STREAMING_ACTIVE_KEY = 'ep_streaming_active';

// Timing constants
const MIN_CHECK_INTERVAL = 60 * 1000; // 60 seconds between checks

/**
 * V25.5.2: NUKE ALL CACHES - Call this when ANY stale content is suspected
 * This is the nuclear option - clears EVERYTHING
 */
export const nukeAllCaches = () => {
  devLog.log('[CACHE V25.5.2] ☢️ NUKING ALL CACHES...');
  
  try {
    // 1. Clear localStorage (except auth)
    const keysToKeep = ['ep_session_token', 'ep_user', 'ep_app_version'];
    const allKeys = Object.keys(localStorage);
    let clearedCount = 0;
    allKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    });
    devLog.log(`[CACHE V25.5.2] Cleared ${clearedCount} localStorage keys`);
    
    // 2. Clear ALL sessionStorage
    const sessionCount = sessionStorage.length;
    sessionStorage.clear();
    devLog.log(`[CACHE V25.5.2] Cleared ${sessionCount} sessionStorage keys`);
    
    // 3. Unregister ALL service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => {
          devLog.log('[CACHE V25.5.2] Unregistering SW:', reg.scope);
          reg.unregister();
        });
      });
    }
    
    // 4. Clear ALL browser caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          devLog.log('[CACHE V25.5.2] Deleting cache:', name);
          caches.delete(name);
        });
      });
    }
    
    devLog.log('[CACHE V25.5.2] ☢️ NUKE COMPLETE - All caches cleared');
    return true;
  } catch (e) {
    console.error('[CACHE V25.5.2] Nuke failed:', e);
    return false;
  }
};

/**
 * Mark streaming as active (call when streaming starts)
 * V25.3.0: Integrated with silentUpdateManager
 */
export const markStreamingActive = () => {
  sessionStorage.setItem(STREAMING_ACTIVE_KEY, 'true');
  silentUpdateManager.startStreaming();
  devLog.log('[CACHE V25.3.0] Streaming marked as ACTIVE - updates blocked');
};

/**
 * Mark streaming as inactive (call when streaming completes)
 * V25.3.0: Integrated with silentUpdateManager
 */
export const markStreamingInactive = () => {
  sessionStorage.removeItem(STREAMING_ACTIVE_KEY);
  silentUpdateManager.endStreaming();
  devLog.log('[CACHE V25.3.0] Streaming marked as INACTIVE - updates can activate');
};

// V25.3.0: Legacy pending update functions REMOVED - now handled by silentUpdateManager

/**
 * Check if enough time has passed since last check
 */
const canCheckVersion = () => {
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  if (!lastCheck) return true;
  
  const timeSinceLastCheck = Date.now() - parseInt(lastCheck, 10);
  if (timeSinceLastCheck < MIN_CHECK_INTERVAL) {
    devLog.debug(`[CACHE V25.3.0] Too soon to check (${Math.round(timeSinceLastCheck/1000)}s)`);
    return false;
  }
  
  return true;
};

/**
 * Check if a new version is available (with debouncing)
 * V24.79: Uses backend /api/version endpoint instead of static file
 * @returns {Promise<{hasUpdate: boolean, currentVersion: string, newVersion: string}>}
 */
export const checkForUpdates = async () => {
  // SAFEGUARD: Don't check if too soon
  if (!canCheckVersion()) {
    return { hasUpdate: false, currentVersion: BUILD_VERSION, newVersion: BUILD_VERSION };
  }
  
  try {
    // Record check time BEFORE fetching to prevent race conditions
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
    
    // V24.79: Use backend /api/version endpoint with cache-busting
    const cacheBuster = Date.now();
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${backendUrl}/api/version?_=${cacheBuster}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      devLog.warn('[CACHE V24.79] Failed to fetch /api/version');
      return { hasUpdate: false, currentVersion: BUILD_VERSION, newVersion: BUILD_VERSION };
    }
    
    const versionData = await response.json();
    const serverVersion = versionData.version;
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    
    devLog.log(`[CACHE V24.79] Version check: frontend=${BUILD_VERSION}, backend=${serverVersion}, stored=${storedVersion}`);
    
    // Only consider it an update if versions are different AND we have a stored version
    const hasUpdate = storedVersion && serverVersion !== storedVersion;
    
    devLog.log(`[CACHE] Version check: stored=${storedVersion}, server=${serverVersion}, hasUpdate=${hasUpdate}`);
    
    return {
      hasUpdate,
      currentVersion: storedVersion || BUILD_VERSION,
      newVersion: serverVersion
    };
  } catch (error) {
    console.error('[CACHE] Error checking for updates:', error);
    return { hasUpdate: false, currentVersion: BUILD_VERSION, newVersion: BUILD_VERSION };
  }
};

/**
 * V21.3: Silent update handler - NO POPUP
 * Stores pending update and applies when safe
 */
export const showUpdateNotification = (newVersion) => {
  // V25.3.0: Handled by silentUpdateManager - just log and update version
  devLog.log(`[CACHE V25.3.0] Update available: ${newVersion} - delegating to silentUpdateManager`);
  localStorage.setItem(CACHE_VERSION_KEY, newVersion);
};

/**
 * Safe reload - V25.3.0: DISABLED permanently
 * All updates now handled by silentUpdateManager without reloads
 */
export const safeReload = async (newVersion) => {
  devLog.log('[CACHE V25.3.0] ❌ safeReload DISABLED - use silentUpdateManager');
  localStorage.setItem(CACHE_VERSION_KEY, newVersion);
  return false;
};

/**
 * V24.81: Clear all result-related caches
 * Called when version changes to prevent stale content
 */
export const clearResultCaches = () => {
  devLog.log('[CACHE V24.81] Clearing all result caches...');
  
  const keysToRemove = [];
  
  // Find all result-related keys in localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith('result_') ||
      key.startsWith('ep_') ||
      key.includes('bug_fix') ||
      key.includes('project')
    )) {
      keysToRemove.push(key);
    }
  }
  
  // Remove them
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    devLog.log(`[CACHE V24.81] Cleared: ${key}`);
  });
  
  // Clear sessionStorage too
  const sessionKeysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (
      key.startsWith('result_') ||
      key.startsWith('ep_') ||
      key.includes('bug_fix') ||
      key.includes('project')
    )) {
      sessionKeysToRemove.push(key);
    }
  }
  
  sessionKeysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
    devLog.log(`[CACHE V24.81] Cleared session: ${key}`);
  });
  
  devLog.log(`[CACHE V24.81] Cleared ${keysToRemove.length} localStorage + ${sessionKeysToRemove.length} sessionStorage result caches`);
};

/**
 * Initialize cache management - SAFE VERSION
 * NO automatic reloads - only logs and tracks state
 * V24.81: Added aggressive cache clearing on version change
 */
/**
 * Initialize cache management - V25.3.0 SILENT UPDATE SYSTEM
 * NO window.location.reload() - EVER
 * Updates activate on safe navigation/idle only
 */
export const initCacheManagement = async () => {
  devLog.log(`[CACHE V25.3.0] ═══════════════════════════════════════`);
  devLog.log(`[CACHE V25.3.0] App version: ${BUILD_VERSION} (${BUILD_ID})`);
  devLog.log(`[CACHE V25.3.0] Build timestamp: ${BUILD_TIMESTAMP}`);
  devLog.log(`[CACHE V25.3.0] Silent updates enabled - NO RELOADS`);
  devLog.log(`[CACHE V25.3.0] ═══════════════════════════════════════`);
  
  // Initialize silent update manager
  silentUpdateManager.initSilentUpdateManager();
  
  // Store current version if not set
  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
  
  // V25.3.0: If version changed, clear caches but NO RELOAD
  if (storedVersion && storedVersion !== BUILD_VERSION) {
    devLog.log(`[CACHE V25.3.0] 🔄 NEW DEPLOYMENT DETECTED: ${storedVersion} -> ${BUILD_VERSION}`);
    devLog.log(`[CACHE V25.3.0] Clearing stale result caches...`);
    clearResultCaches();
    
    // Clear blocking cookies automatically
    clearBlockingCookies();
    
    // Clear stale storage
    clearStaleStorage();
    
    localStorage.setItem(CACHE_VERSION_KEY, BUILD_VERSION);
    devLog.log(`[CACHE V25.3.0] ✅ Cache cleared, updated to: ${BUILD_VERSION}`);
  } else if (!storedVersion) {
    localStorage.setItem(CACHE_VERSION_KEY, BUILD_VERSION);
    devLog.log('[CACHE V25.3.0] First visit - stored version:', BUILD_VERSION);
  }
  
  // V25.3.0: Version checks now handled by silentUpdateManager
  devLog.log('[CACHE V25.3.0] Version checks delegated to silentUpdateManager');
};

/**
 * V32.4.0: Clear blocking cookies on deployment
 * Does NOT touch session_token or auth cookies (preserves login)
 */
const clearBlockingCookies = () => {
  const blockingPatterns = ['credit_', 'pending_', 'lock_', '_csrf', 'XSRF'];
  const domains = ['', window.location.hostname];
  
  try {
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      const lower = name.toLowerCase();
      if (blockingPatterns.some(p => lower.includes(p))) {
        domains.forEach(domain => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          if (domain) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
          }
        });
      }
    });
    devLog.log('[CACHE V32.4.0] Blocking cookies cleared (auth preserved)');
  } catch (e) {
    devLog.warn('[CACHE V32.4.0] Cookie clear error:', e);
  }
};

/**
 * V24.99.32: Clear stale storage automatically on deployment
 */
const clearStaleStorage = () => {
  const preserveKeys = ['ep_session_token', 'ep_session_backup', 'ep_user_email', 'ep_cache_version'];
  // V24.99.34: Add credit-related patterns to clear
  const stalePatterns = [
    'ep_result_', 
    'ep_streaming_', 
    'ep_magic_', 
    'elonprompt_',
    'ep_credit_',
    'ep_deduct_',
    'credit_cache_',
    'pending_credit_',
    'deduction_'
  ];
  
  try {
    Object.keys(localStorage).forEach(key => {
      if (preserveKeys.includes(key)) return;
      if (stalePatterns.some(p => key.startsWith(p) || key.includes(p))) {
        localStorage.removeItem(key);
      }
    });
    
    Object.keys(sessionStorage).forEach(key => {
      if (stalePatterns.some(p => key.startsWith(p) || key.includes(p))) {
        sessionStorage.removeItem(key);
      }
    });
    devLog.log('[CACHE V24.99.34] ✅ Stale storage cleared (including credit cache)');
  } catch (e) {
    devLog.warn('[CACHE V24.99.34] Storage clear error:', e);
  }
};

/**
 * V24.99.34: Clear credit-related cache specifically
 * Called to fix credit deduction regression issues
 */
export const clearCreditCache = () => {
  const creditPatterns = ['credit', 'deduct', 'pending_credit', 'ep_credit'];
  try {
    Object.keys(localStorage).forEach(key => {
      if (creditPatterns.some(p => key.toLowerCase().includes(p))) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach(key => {
      if (creditPatterns.some(p => key.toLowerCase().includes(p))) {
        sessionStorage.removeItem(key);
      }
    });
    devLog.log('[CACHE V25.0.2] ✅ Credit cache cleared');
  } catch (e) {
    devLog.warn('[CACHE V25.0.2] Credit cache clear error:', e);
  }
};

/**
 * V25.0.2: AUTOMATIC CACHE CLEAR ON EVERY PAGE LOAD
 * Runs silently to prevent stale content issues
 * This ensures credit deduction works consistently
 */
export const autoCleanCacheOnLoad = () => {
  devLog.log('[CACHE V25.0.2] 🧹 Running automatic cache cleaning...');
  
  // Clear potentially blocking storage items
  const problematicPatterns = [
    'credit_pending',
    'deduction_in_progress',
    'ep_generation_lock',
    'ep_session_lock',
    'stale_',
    '_temp_',
    '_cache_'
  ];
  
  try {
    // Clean localStorage
    Object.keys(localStorage).forEach(key => {
      if (problematicPatterns.some(p => key.includes(p))) {
        localStorage.removeItem(key);
        devLog.log(`[CACHE V25.0.2] Cleared localStorage: ${key}`);
      }
    });
    
    // Clean sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (problematicPatterns.some(p => key.includes(p))) {
        sessionStorage.removeItem(key);
        devLog.log(`[CACHE V25.0.2] Cleared sessionStorage: ${key}`);
      }
    });
    
    // Clear service worker caches (non-blocking)
    if ('caches' in window) {
      caches.keys().then(names => {
        const oldCaches = names.filter(name => 
          name.includes('precache') || 
          name.includes('runtime') ||
          name.includes('workbox')
        );
        oldCaches.forEach(name => {
          caches.delete(name);
          devLog.log(`[CACHE V25.0.2] Cleared SW cache: ${name}`);
        });
      }).catch(() => {});
    }
    
    // Unregister old service workers (non-blocking)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      }).catch(() => {});
    }
    
    devLog.log('[CACHE V25.0.2] ✅ Auto cache clean complete');
  } catch (e) {
    devLog.warn('[CACHE V25.0.2] Auto cache clean error:', e);
  }
};

/**
 * V25.0.4: DESKTOP ZOOM - DISABLED
 * The CSS zoom property causes layout issues (content shifts left)
 * Removing zoom to maintain proper centered layout
 */
export const initDesktopZoom = () => {
  // V25.0.4: Zoom disabled - causes layout issues
  // Remove any existing zoom styles
  const existing = document.getElementById('desktop-zoom-v2503');
  if (existing) existing.remove();
  const oldExisting = document.getElementById('desktop-zoom-v2502');
  if (oldExisting) oldExisting.remove();
  
  // Reset zoom data attribute
  const root = document.documentElement;
  root.dataset.zoomApplied = 'false';
  
  // Remove any inline zoom
  root.style.zoom = '';
  document.body.style.zoom = '';
  
  devLog.log('[ZOOM V25.0.4] Desktop zoom DISABLED - layout preserved');
};

/**
 * V25.0.2: Clear all cookies that might block functionality
 * Called automatically on init to fix credit deduction issues
 */
export const clearAllBlockingCookies = () => {
  devLog.log('[CACHE V25.0.2] 🍪 Clearing all blocking cookies...');
  
  try {
    // Get all cookies
    const cookies = document.cookie.split(';');
    
    // Patterns of cookies to clear
    const blockingPatterns = [
      'session_token',
      'ep_session',
      'auth_token',
      'oauth_',
      '_csrf',
      'XSRF',
      'credit_',
      'pending_',
      'lock_'
    ];
    
    // Clear cookies on all possible domains
    const domains = ['', '.elonprompt.ai', '.preview.emergentagent.com', window.location.hostname];
    const paths = ['/', '/api', '/auth'];
    
    cookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      
      // Check if this cookie should be cleared
      const shouldClear = blockingPatterns.some(p => cookieName.toLowerCase().includes(p.toLowerCase()));
      
      if (shouldClear) {
        domains.forEach(domain => {
          paths.forEach(path => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
            if (domain) {
              document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
            }
          });
        });
        devLog.log(`[CACHE V25.0.2] Cleared cookie: ${cookieName}`);
      }
    });
    
    devLog.log('[CACHE V25.0.2] ✅ Blocking cookies cleared');
  } catch (e) {
    devLog.warn('[CACHE V25.0.2] Cookie clear error:', e);
  }
};

/**
 * Manual cache clear (for Settings page or troubleshooting)
 */
export const handleManualCacheClear = async () => {
  if (!window.confirm('This will clear all cached data and reload the app. Continue?')) {
    return false;
  }
  
  // Reset all tracking
  localStorage.removeItem(CACHE_VERSION_KEY);
  localStorage.removeItem(LAST_CHECK_KEY);
  // V25.3.0: PENDING_UPDATE_KEY removed - handled by silentUpdateManager
  sessionStorage.removeItem(STREAMING_ACTIVE_KEY);
  
  return safeReload(BUILD_VERSION);
};

export default {
  BUILD_VERSION,
  BUILD_TIMESTAMP,
  BUILD_ID,
  checkForUpdates,
  initCacheManagement,
  handleManualCacheClear,
  safeReload,
  showUpdateNotification,
  markStreamingActive,
  markStreamingInactive,
  clearCreditCache,
  clearResultCaches,
  autoCleanCacheOnLoad,
  initDesktopZoom,
  clearAllBlockingCookies
};
